import { lazy, Suspense, useState } from 'react';
import {
  useCreateNoteComment,
  useCreateTaskComment,
  useDeleteComment,
  useMyWorkspaceMembership,
  useNoteComments,
  useTaskComments,
  useUpdateComment,
} from '@fluxter/shared';
import { Pencil, Trash2 } from 'lucide-react';
import { MemberAvatar } from '@/components/MemberAvatar';
import { UserName } from '@/components/UserName';
import { useConfirm } from '@/components/ConfirmDialog';
import { Button } from '@/components/ui/button';
import { formatRelativeTime } from '@/lib/relative-time';
import { cn } from '@/lib/utils';

/**
 * The CommentEditor itself loads TipTap, so let it land in the
 * shared lazy chunk alongside NoteEditor. Visiting a task / note
 * page that has comments but no live composer (read-only viewing)
 * shouldn't fetch the editor unless the user clicks the composer or
 * Edit on a row.
 */
const CommentEditor = lazy(() =>
  import('@/components/CommentEditor').then((m) => ({ default: m.CommentEditor })),
);

interface CommentAttrs {
  body?: string;
  user_id?: string;
  author_name?: string | null;
  author_email?: string | null;
  edited_at?: string | null;
  created_at?: string | null;
}

interface CommentItem {
  id: string;
  attributes?: CommentAttrs;
}

interface CommentsPayload {
  data?: CommentItem[];
}

interface CommentCreateResult {
  data?: { id?: string };
}

export interface CommentThreadProps {
  workspaceSlug: string;
  parent: { type: 'task'; taskUlid: string } | { type: 'note'; noteUlid: string };
}

export const CommentThread = ({ workspaceSlug, parent }: CommentThreadProps) => {
  // Two parallel hook calls — only one resolves to actual data because
  // the other has `enabled: false` via its undefined parameter. Cleaner
  // than branching on parent.type at every callsite below.
  const taskComments = useTaskComments(
    workspaceSlug,
    parent.type === 'task' ? parent.taskUlid : undefined,
  );
  const noteComments = useNoteComments(
    workspaceSlug,
    parent.type === 'note' ? parent.noteUlid : undefined,
  );
  const createTaskComment = useCreateTaskComment(
    workspaceSlug,
    parent.type === 'task' ? parent.taskUlid : '',
  );
  const createNoteComment = useCreateNoteComment(
    workspaceSlug,
    parent.type === 'note' ? parent.noteUlid : '',
  );
  const updateComment = useUpdateComment(workspaceSlug);
  const deleteComment = useDeleteComment(workspaceSlug);
  const membership = useMyWorkspaceMembership(workspaceSlug);

  const query = parent.type === 'task' ? taskComments : noteComments;
  const rows = (query.data as CommentsPayload | undefined)?.data ?? [];

  const [composerBody, setComposerBody] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [composerError, setComposerError] = useState<string | null>(null);

  const isComposerEmpty = stripHtmlForCheck(composerBody).length === 0;

  const handleSubmit = async (): Promise<void> => {
    if (isComposerEmpty || submitting) return;
    setComposerError(null);
    setSubmitting(true);
    try {
      const result =
        parent.type === 'task'
          ? ((await createTaskComment.mutateAsync({ body: composerBody })) as CommentCreateResult)
          : ((await createNoteComment.mutateAsync({ body: composerBody })) as CommentCreateResult);
      if (!result?.data?.id) {
        setComposerError('Comment saved but the response was malformed.');
        return;
      }
      setComposerBody('');
    } catch (e) {
      setComposerError(e instanceof Error ? e.message : 'Could not post comment.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="bg-card space-y-4 rounded-lg border p-6">
      <header className="flex items-center justify-between gap-2">
        <h2 className="text-sm font-semibold tracking-tight">Comments</h2>
        {rows.length > 0 ? (
          <span className="text-muted-foreground text-xs">
            {rows.length} {rows.length === 1 ? 'comment' : 'comments'}
          </span>
        ) : null}
      </header>

      {query.isLoading ? (
        <p className="text-muted-foreground text-sm">Loading…</p>
      ) : rows.length > 0 ? (
        <ul className="space-y-4">
          {rows.map((c) => (
            <li key={c.id}>
              <CommentRow
                workspaceSlug={workspaceSlug}
                comment={c}
                currentUserId={membership.userId}
                isAdmin={membership.isAdmin}
                onUpdate={async (newBody) => {
                  await updateComment.mutateAsync({ commentId: c.id, body: newBody });
                }}
                onDelete={async () => {
                  await deleteComment.mutateAsync(c.id);
                }}
              />
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-muted-foreground text-sm italic">
          No comments yet — start the thread.
        </p>
      )}

      <div className="space-y-2 border-t pt-4">
        <Suspense fallback={<ComposerSkeleton />}>
          <CommentEditor
            workspaceSlug={workspaceSlug}
            content=""
            onUpdate={(html) => setComposerBody(html)}
            onSubmitShortcut={() => void handleSubmit()}
          />
        </Suspense>
        {composerError ? (
          <p className="text-destructive text-sm" role="alert">
            {composerError}
          </p>
        ) : null}
        <div className="text-muted-foreground flex items-center justify-between text-xs">
          <span>
            <kbd className="bg-muted rounded border px-1.5 py-0.5 font-mono text-[10px]">
              ⌘
            </kbd>
            +
            <kbd className="bg-muted rounded border px-1.5 py-0.5 font-mono text-[10px]">
              Enter
            </kbd>{' '}
            to send
          </span>
          <Button
            type="button"
            size="sm"
            disabled={isComposerEmpty || submitting}
            onClick={() => void handleSubmit()}
          >
            {submitting ? 'Posting…' : 'Comment'}
          </Button>
        </div>
      </div>
    </section>
  );
};

interface CommentRowProps {
  workspaceSlug: string;
  comment: CommentItem;
  currentUserId: string | null;
  isAdmin: boolean;
  onUpdate: (newBody: string) => Promise<void>;
  onDelete: () => Promise<void>;
}

const CommentRow = ({
  workspaceSlug,
  comment,
  currentUserId,
  isAdmin,
  onUpdate,
  onDelete,
}: CommentRowProps) => {
  const { confirm, dialog } = useConfirm();
  const attrs = comment.attributes ?? {};
  const isAuthor = attrs.user_id === currentUserId;
  const canEdit = isAuthor;
  const canDelete = isAuthor || isAdmin;

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(attrs.body ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const startEdit = (): void => {
    setDraft(attrs.body ?? '');
    setEditing(true);
    setError(null);
  };

  const cancelEdit = (): void => {
    setDraft(attrs.body ?? '');
    setEditing(false);
    setError(null);
  };

  const handleSave = async (): Promise<void> => {
    if (stripHtmlForCheck(draft).length === 0) {
      setError('Comment cannot be empty.');
      return;
    }
    setError(null);
    setSaving(true);
    try {
      await onUpdate(draft);
      setEditing(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save comment.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (): Promise<void> => {
    const ok = await confirm({
      title: 'Delete this comment?',
      description: "This can't be undone.",
      confirmLabel: 'Delete',
      destructive: true,
    });
    if (!ok) return;
    try {
      await onDelete();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not delete comment.');
    }
  };

  return (
    <article className="flex gap-3">
      <MemberAvatar
        id={attrs.user_id ?? null}
        name={attrs.author_name}
        email={attrs.author_email}
        size="sm"
      />
      <div className="min-w-0 flex-1">
        <header className="text-muted-foreground flex items-center gap-2 text-xs">
          <UserName
            workspaceSlug={workspaceSlug}
            userId={attrs.user_id ?? null}
            name={attrs.author_name}
            email={attrs.author_email}
            className="text-foreground font-medium"
          />
          {attrs.created_at ? (
            <span>{formatRelativeTime(attrs.created_at)}</span>
          ) : null}
          {attrs.edited_at ? (
            <span className="text-muted-foreground/70 italic">(edited)</span>
          ) : null}
          <span className="ml-auto flex items-center gap-1">
            {canEdit && !editing ? (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={startEdit}
                className="text-muted-foreground hover:text-foreground h-7 px-2"
                aria-label="Edit comment"
              >
                <Pencil className="size-3.5" aria-hidden="true" />
              </Button>
            ) : null}
            {canDelete && !editing ? (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => void handleDelete()}
                className="text-muted-foreground hover:text-destructive h-7 px-2"
                aria-label="Delete comment"
              >
                <Trash2 className="size-3.5" aria-hidden="true" />
              </Button>
            ) : null}
          </span>
        </header>

        {editing ? (
          <div className="mt-2 space-y-2">
            <Suspense fallback={<ComposerSkeleton />}>
              <CommentEditor
                workspaceSlug={workspaceSlug}
                content={attrs.body ?? ''}
                onUpdate={(html) => setDraft(html)}
                onSubmitShortcut={() => void handleSave()}
                autoFocus
              />
            </Suspense>
            {error ? (
              <p className="text-destructive text-sm" role="alert">
                {error}
              </p>
            ) : null}
            <div className="flex items-center justify-end gap-2">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={cancelEdit}
                disabled={saving}
              >
                Cancel
              </Button>
              <Button type="button" size="sm" onClick={() => void handleSave()} disabled={saving}>
                {saving ? 'Saving…' : 'Save'}
              </Button>
            </div>
          </div>
        ) : (
          <CommentBodyRender html={attrs.body ?? ''} />
        )}

        {!editing && error ? (
          <p className="text-destructive mt-1 text-sm" role="alert">
            {error}
          </p>
        ) : null}
      </div>
      {dialog}
    </article>
  );
};

/**
 * Read-only render of a comment body. We use `dangerouslySetInnerHTML`
 * (rather than instantiating a TipTap editor per row) because a
 * thread of 30 comments would otherwise spawn 30 ProseMirror states
 * for static text. The HTML is already sanitized by TipTap's schema
 * on write — the API only accepts strings, but anything the editor
 * doesn't recognize is stripped on parse next time, so injection
 * surface is the editor schema itself.
 */
const CommentBodyRender = ({ html }: { html: string }) => (
  <div
    className={cn(
      'text-foreground/90 mt-1 text-sm leading-relaxed',
      '[&>*:first-child]:mt-0 [&>*:last-child]:mb-0',
      '[&_p]:my-1',
      '[&_ul]:my-1 [&_ul]:list-disc [&_ul]:pl-5',
      '[&_ol]:my-1 [&_ol]:list-decimal [&_ol]:pl-5',
      '[&_li]:my-0.5',
      '[&_li>p]:my-0',
      '[&_code]:bg-muted [&_code]:rounded [&_code]:px-1 [&_code]:py-0.5 [&_code]:font-mono [&_code]:text-[0.85em]',
      '[&_blockquote]:text-muted-foreground [&_blockquote]:my-1 [&_blockquote]:border-l-2 [&_blockquote]:pl-3',
      '[&_a]:text-primary [&_a]:underline-offset-2 [&_a]:hover:underline',
      '[&_span[data-type=mention]]:bg-primary/10 [&_span[data-type=mention]]:text-primary [&_span[data-type=mention]]:rounded [&_span[data-type=mention]]:px-1 [&_span[data-type=mention]]:py-0.5 [&_span[data-type=mention]]:font-medium',
    )}
    dangerouslySetInnerHTML={{ __html: html }}
  />
);

const ComposerSkeleton = () => (
  <div
    className="bg-muted/30 min-h-[80px] rounded-md border"
    role="status"
    aria-live="polite"
  />
);

/**
 * Quick "is this body actually empty?" check that strips the wrapper
 * `<p>` TipTap inserts for an otherwise-empty document. We don't need
 * full HTML parsing here — just enough to know whether to enable the
 * Send button.
 */
const stripHtmlForCheck = (html: string): string =>
  html
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/g, ' ')
    .trim();
